import { useEffect, useState } from 'react';
import { tokens } from '../../components/forms/tokens';
import { fullName } from '../../lib/fullName';
import { cleanMfaInput, stepUpErrorMessage } from '../../lib/mfaApproval';
import { ROLE_LABEL } from '../../lib/roleRouting';
import adminService, { type AdminUserSummary } from '../../services/admin.service';
import { useMovablePopup } from '../../components/MovablePopup';
import {
  COMMON_TIMEZONES,
  extractError,
} from './_userActions';
import {
  type CreatableStaffRole,
  type CreateStaffForm,
  createStaffFormErrors,
  creationNeedsSecondAdmin,
  emptyCreateStaffForm,
  toCreateProposalBody,
  toCreateStaffBody,
} from './createStaff.helpers';

/** Roles this dialog can create. Clients come through /invitations. An agent is created at
 *  once; a manager or a super admin is proposed here and confirmed by a different super admin. */
const CREATE_ROLES: CreatableStaffRole[] = ['agent', 'manager', 'super_admin'];

const PROPOSED_MESSAGE =
  'Proposed. A different super admin confirms it under Changes waiting for a second super admin.';

/**
 * "Add staff" modal. Distinct from the per-row ActionDialog in
 * _userActions: that one mutates an existing user_id; this one provisions
 * a brand-new internal account (POST /admin/users). Staff authenticate via
 * Google SSO, so no password is set here — the employee signs in with the
 * matching Google Workspace email.
 */
export default function CreateStaffDialog({
  activeManagers,
  onClose,
  onDone,
}: Readonly<{
  activeManagers: AdminUserSummary[];
  onClose: () => void;
  onDone: (msg: string) => void;
}>): JSX.Element {
  const popup = useMovablePopup<HTMLDialogElement>();
  const dialogRef = popup.panelRef;
  // Open via showModal() on mount. Deliberately NO close() in the cleanup: on a
  // real unmount React removes the <dialog> node (which closes it natively),
  // whereas a programmatic close() fires the native 'close' event — and under
  // React 18 StrictMode the dev-only mount→unmount→remount routes that event to
  // onClose, slamming the modal shut the instant it opens. The `!open` guard
  // stops the StrictMode re-mount from calling showModal() on an already-open
  // dialog (which throws InvalidStateError).
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, [dialogRef]);

  const [form, setForm] = useState<CreateStaffForm>(emptyCreateStaffForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof CreateStaffForm>(key: K, value: CreateStaffForm[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));

  const errors = createStaffFormErrors(form);
  const submitDisabled = busy || errors.length > 0;
  const proposes = creationNeedsSecondAdmin(form.role);
  let submitLabel = proposes ? 'Propose' : 'Create staff';
  if (busy) submitLabel = 'Working…';

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      if (proposes) {
        await adminService.proposeStaffChange(toCreateProposalBody(form));
        onDone(PROPOSED_MESSAGE);
      } else {
        const resp = await adminService.createStaff(toCreateStaffBody(form));
        onDone(resp.message ?? 'Staff account created.');
      }
    } catch (e) {
      setErr(stepUpErrorMessage(e, extractError(e)));
    } finally {
      setBusy(false);
    }
  };

  const labelCls = 'block text-xs font-semibold mb-1';
  const inputCls = 'w-full px-3 py-2 rounded-lg text-sm';
  const inputStyle = { border: `1px solid ${tokens.border}` };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onCancel={onClose}
      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto backdrop:bg-black/45"
      style={{ ...popup.panelStyle, background: tokens.surface, border: 'none' }}
    >
      {popup.bar}
      <h2 className="text-lg font-semibold mb-1" style={{ color: tokens.textPrimary }}>
        Add staff account
      </h2>
      <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>
        Provisions an internal staff account. The user signs in with
        Google SSO using the email below — no password is set here.
      </p>
      {proposes && (
        <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>
          A manager or a super admin takes two super admins: you propose it, and a different
          super admin confirms it with their code before the account exists.
        </p>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className={labelCls} style={{ color: tokens.textLabel }}>First name</span>
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            maxLength={100}
            className={inputCls}
            style={inputStyle}
          />
        </label>
        <label className="block">
          <span className={labelCls} style={{ color: tokens.textLabel }}>Last name</span>
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => set('lastName', e.target.value)}
            maxLength={100}
            className={inputCls}
            style={inputStyle}
          />
        </label>
      </div>

      <label className="block mb-3">
        <span className={labelCls} style={{ color: tokens.textLabel }}>
          Google Workspace email
        </span>
        <input
          type="email"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder="employee@qmi-investments.com"
          className={`${inputCls} font-mono`}
          style={inputStyle}
          autoComplete="off"
        />
      </label>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className={labelCls} style={{ color: tokens.textLabel }}>Role</span>
          <select
            value={form.role}
            onChange={(e) => set('role', e.target.value as CreatableStaffRole)}
            className={inputCls}
            style={inputStyle}
          >
            {CREATE_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={labelCls} style={{ color: tokens.textLabel }}>
            Timezone {form.role === 'agent' ? '' : '(optional)'}
          </span>
          <select
            value={COMMON_TIMEZONES.includes(form.timezone) ? form.timezone : ''}
            onChange={(e) => set('timezone', e.target.value)}
            className={inputCls}
            style={inputStyle}
          >
            <option value="">{form.role === 'agent' ? '— Use platform default —' : '— None —'}</option>
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </label>
      </div>

      {form.role === 'agent' && (
        <label className="block mb-3">
          <span className={labelCls} style={{ color: tokens.textLabel }}>
            Reporting manager (optional)
          </span>
          <select
            value={form.managerId}
            onChange={(e) => set('managerId', e.target.value)}
            className={inputCls}
            style={inputStyle}
          >
            <option value="">— None (rejection emails fan out to all managers) —</option>
            {activeManagers.map((m) => (
              <option key={m.id} value={m.id}>
                {fullName(m)} ({m.email})
              </option>
            ))}
          </select>
          {activeManagers.length === 0 && (
            <span className="block mt-1 text-xs" style={{ color: tokens.textMuted }}>
              No active managers loaded on this page — filter to &quot;Manager&quot; first to pick one,
              or assign later from the row menu.
            </span>
          )}
        </label>
      )}

      <label className="block mb-3">
        <span className={labelCls} style={{ color: tokens.textLabel }}>
          Reason (mandatory · stored in audit log)
        </span>
        <textarea
          value={form.reason}
          onChange={(e) => set('reason', e.target.value)}
          rows={2}
          maxLength={500}
          className={inputCls}
          style={inputStyle}
          placeholder="e.g. new hire — onboarding ticket HR-1234"
        />
      </label>

      <label className="block mb-1">
        <span className={labelCls} style={{ color: tokens.textLabel }}>
          Confirm with the code from your authenticator app
        </span>
        <input
          value={form.mfaCode}
          onChange={(e) => set('mfaCode', cleanMfaInput(e.target.value))}
          inputMode="numeric"
          autoComplete="one-time-code"
          className="w-40 px-3 py-2 rounded-lg text-sm"
          style={inputStyle}
        />
      </label>

      {err && (
        <div
          className="mt-3 p-3 rounded-lg text-sm"
          style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitDisabled}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: submitDisabled ? tokens.disabledBg : tokens.infoText,
            color: submitDisabled ? tokens.disabledText : '#FFFFFF',
            cursor: submitDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {submitLabel}
        </button>
      </div>
    </dialog>
  );
}
